import axios from 'axios'
import { keycloakUrl, keycloakRN } from './keycloak.service'
import { baseURL } from './http.service'

// ROPC (Direct Access Grants) — přihlášení přímo přes token endpoint Keycloaku,
// stejný princip jako mobilní aplikace. Keycloak zůstává jen auth backend.

const TOKEN_ENDPOINT = `${keycloakUrl}/realms/${keycloakRN}/protocol/openid-connect/token`

export const RESET_PASSWORD_URL = `${keycloakUrl}/realms/${keycloakRN}/login-actions/reset-credentials?client_id=itrader-front`

export interface StoredTokens {
  token: string
  refreshToken: string
  idToken?: string
}

export type AuthErrorKind = 'bad_credentials' | 'update_password' | 'disabled' | 'network' | 'unknown'

export class AuthError extends Error {
  kind: AuthErrorKind

  constructor(kind: AuthErrorKind, message: string) {
    super(message)
    this.kind = kind
  }
}

export const persistTokens = (tokens: { token?: string; refreshToken?: string; idToken?: string }) => {
  if (tokens.token) localStorage.setItem('kc_token', tokens.token)
  if (tokens.refreshToken) localStorage.setItem('kc_refreshToken', tokens.refreshToken)
  if (tokens.idToken) localStorage.setItem('kc_idToken', tokens.idToken)
}

export const loadStoredTokens = (): StoredTokens | undefined => {
  const token = localStorage.getItem('kc_token')
  const refreshToken = localStorage.getItem('kc_refreshToken')
  if (!token || !refreshToken) {
    return undefined
  }
  return { token, refreshToken, idToken: localStorage.getItem('kc_idToken') || undefined }
}

export const clearStoredTokens = () => {
  localStorage.removeItem('kc_token')
  localStorage.removeItem('kc_idToken')
  localStorage.removeItem('kc_refreshToken')
}

const toAuthError = (err: any): AuthError => {
  const data = err?.response?.data
  if (!data) {
    return new AuthError('network', 'Nepodařilo se spojit se serverem. Zkuste to prosím znovu.')
  }
  const description: string = data.error_description || ''
  if (description.includes('not fully set up')) {
    return new AuthError('update_password', 'Z bezpečnostních důvodů si prosím nastavte nové heslo.')
  }
  if (description.includes('Account disabled')) {
    return new AuthError('disabled', 'Účet je zablokovaný. Kontaktujte prosím podporu.')
  }
  if (data.error === 'invalid_grant' || data.error === 'invalid_client') {
    return new AuthError('bad_credentials', 'Nesprávné jméno nebo heslo.')
  }
  return new AuthError('unknown', 'Přihlášení se nezdařilo. Zkuste to prosím znovu.')
}

const requestTokens = async (form: URLSearchParams): Promise<StoredTokens> => {
  const { data } = await axios.post(TOKEN_ENDPOINT, form, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  })
  return { token: data.access_token, refreshToken: data.refresh_token, idToken: data.id_token }
}

// „Zůstat přihlášen" = offline_access scope -> dlouhodobý refresh token nevázaný na SSO idle.
export const ropcLogin = async (username: string, password: string, rememberMe: boolean): Promise<StoredTokens> => {
  const form = new URLSearchParams()
  form.set('grant_type', 'password')
  form.set('client_id', 'itrader-front')
  form.set('username', username)
  form.set('password', password)
  form.set('scope', rememberMe ? 'openid offline_access' : 'openid')

  try {
    const tokens = await requestTokens(form)
    persistTokens(tokens)
    return tokens
  } catch (err: any) {
    if (rememberMe && err?.response?.data?.error === 'invalid_scope') {
      // offline_access není povolen — přihlásit aspoň bez něj
      return ropcLogin(username, password, false)
    }
    throw toAuthError(err)
  }
}

// První přihlášení s dočasným heslem: Keycloak vyžaduje UPDATE_PASSWORD a token nevydá.
// Backend ověří dočasné heslo a přes Admin API nastaví trvalé, pak se přihlásíme znovu.
export const completeFirstLogin = async (
  username: string,
  temporaryPassword: string,
  newPassword: string,
  rememberMe: boolean
): Promise<StoredTokens> => {
  try {
    await axios.post(`${baseURL}/auth/first-password`, {
      username,
      temporaryPassword,
      newPassword,
    })
  } catch (err: any) {
    const status = err?.response?.status
    if (status === 401) {
      throw new AuthError('bad_credentials', 'Nesprávné jméno nebo heslo.')
    }
    if (status === 400 && err?.response?.data?.message) {
      throw new AuthError('unknown', err.response.data.message)
    }
    if (!err?.response) {
      throw new AuthError('network', 'Nepodařilo se spojit se serverem. Zkuste to prosím znovu.')
    }
    throw new AuthError('unknown', 'Nastavení hesla se nezdařilo. Zkuste to prosím znovu.')
  }
  return ropcLogin(username, newPassword, rememberMe)
}
